import { useEffect, useState } from "react";
import FormContainer from "components/form-container";
import { Input } from "components/input";
import { getQueryParams, isValidEmail } from "lib/utils";
import { useTranslation } from "react-i18next";
import { useLocation, useOutletContext } from "react-router-dom";
import MessageBanner from "components/message-banner";
import { EmailValidation } from "components/email-validation";

const { REACT_APP_API } = process.env;

export default function PasswordResetPage() {
  const { t } = useTranslation();
  const { emailInput } = useOutletContext();
  const [email, setEmail] = useState(emailInput || "");
  const [isComplete, setIsComplete] = useState(false);
  const [completeEmail, setCompleteEmail] = useState("");
  const [isResetDisabled, setIsResetDisabled] = useState(false);
  const [emailAlarm, setEmailAlarm] = useState(false);

  // Reset right side visual
  const { setSidebarVisual, setEmailInput } = useOutletContext();
  useEffect(() => {
    setSidebarVisual("man");
  }, [setSidebarVisual]);

  // Get query params
  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setEmail(newValue);
    setEmailInput(newValue);
    setEmailAlarm(!isValidEmail(newValue));
  };

  const handleSubmit = async () => {
    if (!isValidEmail(email)) {
      setEmailAlarm(true);
      return;
    }

    setIsResetDisabled(true);
    try {
      const response = await fetch(`${REACT_APP_API}/password/forgot`, {
        method: "POST",
        body: JSON.stringify({
          email,
          returnToPath: `${queryParams.returnToPath}?updatedPassword=true`,
          passwordUpdatePath: t("urls.password_update"),
        }),
      });

      if (!response.ok) {
        throw new Error();
      }
      setIsComplete(true);
      setCompleteEmail(email);
    } catch (error) {
      setIsResetDisabled(false);
      console.error(error);
      alert("Please try again");
    }
  };

  const updatedParams = { ...queryParams, email };

  return (
    <FormContainer
      submitLabel="Request Password Reset"
      onSubmit={handleSubmit}
      submitDisabled={!email || isResetDisabled || emailAlarm}
      secondaryLabel={t("ui.button_back")}
      secondaryHref={t("urls.trade_in_email")}
      secondaryParams={updatedParams}
    >
      <div className="flex flex-col gap-regular">
        <h2 className="font-semibold text-6xl max-w-lg">Reset your password</h2>
        <p className="text-tertiary text-2xl">
          If your account exists, we'll send you an email to reset your
          password.
        </p>
      </div>
      <div className="w-full flex flex-col">
        <div>
          <Input
            label={t("ui.input_email")}
            type="email"
            onChange={handleInputChange}
            value={email}
          />
          {emailAlarm && <EmailValidation emailAlarm={emailAlarm} />}
        </div>
      </div>
      {isComplete && (
        <MessageBanner
          colour="green"
          title="Password reset requested"
          body={`We’ve just sent an email to ${completeEmail} with a link to reset your password.`}
        />
      )}
    </FormContainer>
  );
}
